import { useState, useEffect, useLayoutEffect, useRef } from "react";
import { Link } from "react-router-dom";
import { Menu, X } from "lucide-react";
import { Button } from "@/components/ui/button";
import { supabase } from "@/integrations/supabase/client";
import { SearchDialog } from "@/components/search/SearchDialog";
import { PromoBanner } from "@/components/misc/PromoBanner";
import { useWishlistStore } from "@/store/wishlistStore";
import { useStaticTranslation } from "@/lib/staticTranslations";
import { translateMenu, loadMenuTranslations } from "@/lib/menuTranslations";
import { getTranslateLang } from "@/lib/translateUtils";
import logo from "@/assets/logo-alice-new.png";

import { useHeaderMenu } from "./header/useHeaderMenu";
import { HeaderIconsDesktop, HeaderIconsMobileRight } from "./header/HeaderIcons";
import { HeaderDesktopNav } from "./header/HeaderDesktopNav";
import { HeaderMobileNav } from "./header/HeaderMobileNav";
import type { ServiceMenuLink } from "./header/types";

interface HeaderProps {
  forceShowShadow?: boolean;
  activeCategoryHandle?: string | null;
}

const Header = ({ forceShowShadow = false, activeCategoryHandle }: HeaderProps) => {
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [isClosing, setIsClosing] = useState(false);
  const [isScrolled, setIsScrolled] = useState(false);
  const [isLoggedIn, setIsLoggedIn] = useState(false);
  const [headerHeight, setHeaderHeight] = useState(0);
  const [, setCurrentLang] = useState(getTranslateLang);
  const headerContainerRef = useRef<HTMLDivElement>(null);

  const { menuSections, isMenuLoading } = useHeaderMenu();
  const { items: wishlistItems, localItems: localWishlistItems } = useWishlistStore();
  const totalWishlistCount = wishlistItems.length + localWishlistItems.length;
  const { t: th } = useStaticTranslation("header");
  const tm = (text: string) => translateMenu(text);

  const serviceMenuLinks: ServiceMenuLink[] = [
    { title: th("contactUs", "צור קשר"), href: "/contact", requiresAuth: false },
    { title: th("faq", "שאלות נפוצות"), href: "/faq", requiresAuth: false },
    { title: th("sizeGuide", "מדריך מידות"), href: "/ring-size-guide", requiresAuth: false },
    { title: "Alice Trade In", href: "/trade-in", requiresAuth: false },
    { title: th("trackRepairs", "מעקב אחרי תיקונים"), href: "/track-order", requiresAuth: false },
  ];

  // Measure full header height (promo banner + header)
  useLayoutEffect(() => {
    const measureHeader = () => {
      if (headerContainerRef.current) {
        setHeaderHeight(headerContainerRef.current.getBoundingClientRect().height);
      }
    };
    measureHeader();
    const resizeObserver = new ResizeObserver(measureHeader);
    if (headerContainerRef.current) resizeObserver.observe(headerContainerRef.current);
    window.addEventListener("resize", measureHeader);
    return () => {
      resizeObserver.disconnect();
      window.removeEventListener("resize", measureHeader);
    };
  }, []);

  // Scroll shadow
  useEffect(() => {
    let ticking = false;
    const updateScrolled = () => {
      const next = window.scrollY > 10;
      setIsScrolled((prev) => (prev === next ? prev : next));
    };
    const handleScroll = () => {
      if (ticking) return;
      ticking = true;
      requestAnimationFrame(() => {
        updateScrolled();
        ticking = false;
      });
    };
    updateScrolled();
    window.addEventListener("scroll", handleScroll, { passive: true });
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  // Auth tracking
  useEffect(() => {
    supabase.auth.getSession().then(({ data: { session } }) => setIsLoggedIn(!!session));
    const { data: { subscription } } = supabase.auth.onAuthStateChange((_event, session) => {
      setIsLoggedIn(!!session);
    });
    return () => subscription.unsubscribe();
  }, []);

  // Menu translations + language polling
  useEffect(() => {
    loadMenuTranslations();
    const interval = setInterval(() => {
      const lang = getTranslateLang();
      setCurrentLang((prev) => (prev !== lang ? lang : prev));
    }, 500);
    return () => clearInterval(interval);
  }, []);

  const handleMenuToggle = () => {
    if (isMenuOpen) {
      setIsClosing(true);
      setTimeout(() => {
        setIsMenuOpen(false);
        setIsClosing(false);
      }, 200);
    } else {
      setIsMenuOpen(true);
    }
  };

  return (
    <>
      <div ref={headerContainerRef} className="fixed top-0 left-0 right-0 z-50">
        <PromoBanner />
        <header
          className={`w-full bg-background border-b border-border/50 transition-shadow duration-300 ${
            isScrolled || forceShowShadow ? "shadow-md" : ""
          }`}
          dir="rtl"
        >
          <div className="w-full px-4 lg:px-8">
            <div className="flex h-20 items-center justify-between">
              {/* Mobile: Menu + Search */}
              <div className="flex lg:hidden items-center gap-1">
                <Button
                  variant="ghost"
                  size="icon"
                  className="hover:bg-transparent"
                  onClick={handleMenuToggle}
                >
                  {isMenuOpen ? (
                    <X className="h-5 w-5 text-[#7e6b44]" />
                  ) : (
                    <Menu className="h-5 w-5 text-[#7e6b44]" />
                  )}
                </Button>
                <SearchDialog />
              </div>

              {/* Logo */}
              <Link to="/" className="flex items-center lg:order-first">
                <img src={logo} alt="ALICE Jewellery | Watches" className="h-12 w-auto" />
              </Link>

              {/* Mobile right: accessibility + cart */}
              <HeaderIconsMobileRight />

              {/* Desktop icons */}
              <HeaderIconsDesktop
                isLoggedIn={isLoggedIn}
                totalWishlistCount={totalWishlistCount}
                th={th}
              />
            </div>
          </div>

          <HeaderDesktopNav
            menuSections={menuSections}
            isMenuLoading={isMenuLoading}
            serviceMenuLinks={serviceMenuLinks}
            isLoggedIn={isLoggedIn}
            activeCategoryHandle={activeCategoryHandle}
            tm={tm}
            th={th}
          />

          <HeaderMobileNav
            isMenuOpen={isMenuOpen}
            isClosing={isClosing}
            headerHeight={headerHeight}
            menuSections={menuSections}
            isMenuLoading={isMenuLoading}
            serviceMenuLinks={serviceMenuLinks}
            isLoggedIn={isLoggedIn}
            totalWishlistCount={totalWishlistCount}
            onClose={handleMenuToggle}
            tm={tm}
            th={th}
          />
        </header>
      </div>
      {/* Spacer to prevent content from being hidden under fixed header */}
      <div
        style={{ height: headerHeight > 0 ? headerHeight : undefined }}
        className={headerHeight > 0 ? "" : "h-24 lg:h-36"}
      />
    </>
  );
};

export default Header;
